// src/Dashboard/pages/user/Inscription/components/RenewalDrawer.jsx

import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaRedoAlt, FaSave } from "react-icons/fa";
import { toast } from "react-toastify";

import DrawerShell from "./DrawerShell";

import {
  updateAdherent,
  updateAdherentGroup,
} from "../../../../../services/adherentServices";
import { getActiveGroupsList } from "../../../../../services/groupServices";
import { getAdherentAvatar, normalizeText } from "../utils/inscriptionUtils";

export default function RenewalDrawer({
  isOpen,
  onClose,
  adherent,
  nextSeason,
}) {
  const [groups, setGroups] = useState([]);
  const [selectedGroupId, setSelectedGroupId] = useState("");
  const [filter, setFilter] = useState("");
  const [birthYears, setBirthYears] = useState("");
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [saving, setSaving] = useState(false);

  const selectRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;

    let cancelled = false;

    const loadGroups = async () => {
      try {
        setLoadingGroups(true);
        const data = await getActiveGroupsList();

        if (!cancelled) {
          setGroups(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        console.error(error);
        toast.error("Impossible de charger les groupes actifs.");
      } finally {
        if (!cancelled) setLoadingGroups(false);
      }
    };

    setSelectedGroupId("");
    setBirthYears("");
    setFilter("");
    loadGroups();

    setTimeout(() => selectRef.current?.focus(), 100);

    return () => {
      cancelled = true;
    };
  }, [isOpen]);

  const filteredGroups = useMemo(() => {
    const q = normalizeText(filter);

    if (!q) return groups;

    return groups.filter((group) =>
      normalizeText(`${group.nom || ""} ${group.annees_naissance || ""}`).includes(q)
    );
  }, [filter, groups]);

  const handleGroupChange = (event) => {
    const groupId = event.target.value;
    setSelectedGroupId(groupId);

    const selectedGroup = groups.find(
      (group) => String(group.id) === String(groupId)
    );

    setBirthYears(selectedGroup?.annees_naissance || "");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!adherent?.id) {
      toast.error("Adhérent introuvable.");
      return;
    }

    if (!selectedGroupId) {
      toast.warn("Veuillez sélectionner un groupe.");
      return;
    }

    try {
      setSaving(true);

      await updateAdherent(adherent.id, {
        saison_active: nextSeason,
        statut_inscription: "Inscrit",
      });

      await updateAdherentGroup(adherent.id, Number(selectedGroupId));

      toast.success("Renouvellement enregistré.");
      onClose?.(true);
    } catch (error) {
      console.error(error);
      toast.error("Erreur lors de l’enregistrement du renouvellement.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <DrawerShell
      isOpen={isOpen}
      onClose={() => onClose?.(false)}
      title="Renouvellement d’adhésion"
      subtitle={`Saison ${nextSeason || "—"}`}
      icon={<FaRedoAlt />}
      width="max-w-2xl"
      footer={
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={() => onClose?.(false)}
            disabled={saving}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-50 disabled:opacity-60 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
          >
            Annuler
          </button>

          <button
            type="submit"
            form="renewal-form"
            disabled={saving || !selectedGroupId}
            className="inline-flex items-center gap-2 rounded-2xl bg-orange-600 px-4 py-3 text-sm font-bold text-white transition hover:bg-orange-700 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <FaSave />
            {saving ? "Enregistrement..." : "Enregistrer"}
          </button>
        </div>
      }
    >
      {!adherent ? null : (
        <form id="renewal-form" onSubmit={handleSubmit} className="space-y-5">
          <div className="rounded-[24px] border border-orange-200 bg-orange-50 p-5 dark:border-orange-500/20 dark:bg-orange-500/10">
            <div className="flex items-center gap-4">
              <img
                src={getAdherentAvatar(adherent)}
                alt={`${adherent.prenom} ${adherent.nom}`}
                className="h-16 w-16 rounded-2xl object-cover ring-1 ring-orange-200 dark:ring-orange-500/20"
              />

              <div>
                <h3 className="text-lg font-bold text-slate-900 dark:text-white">
                  {(adherent.nom || "").toUpperCase()} {adherent.prenom || ""}
                </h3>

                <p className="mt-1 text-sm text-orange-700 dark:text-orange-200">
                  Saison actuelle : {adherent.saison_active || "—"} · Nouvelle
                  saison : {nextSeason || "—"}
                </p>
              </div>
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold text-slate-800 dark:text-slate-100">
              Filtrer les groupes
            </label>

            <input
              value={filter}
              onChange={(event) => setFilter(event.target.value)}
              placeholder="Nom du groupe ou années de naissance..."
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            />
          </div>

          <div>
            <label className="mb-1 block text-sm font-bold text-slate-800 dark:text-slate-100">
              Nouveau groupe
            </label>

            <select
              ref={selectRef}
              value={selectedGroupId}
              onChange={handleGroupChange}
              disabled={loadingGroups}
              className="w-full rounded-2xl border border-slate-300 bg-white px-3 py-3 text-sm text-slate-900 outline-none focus:ring-2 focus:ring-indigo-400 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
            >
              <option value="">
                {loadingGroups ? "Chargement..." : "Sélectionnez un groupe"}
              </option>

              {filteredGroups.map((group) => (
                <option key={group.id} value={group.id}>
                  {group.nom}
                  {group.annees_naissance
                    ? ` · ${group.annees_naissance}`
                    : ""}
                </option>
              ))}
            </select>
          </div>

          {birthYears && (
            <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 dark:border-slate-800 dark:bg-slate-900/60 dark:text-slate-300">
              Années de naissance du groupe :{" "}
              <span className="font-bold">{birthYears}</span>
            </div>
          )}

          <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 text-sm leading-6 text-blue-700 dark:border-blue-500/20 dark:bg-blue-500/10 dark:text-blue-200">
            Le renouvellement mettra l’adhérent sur la saison active, avec le
            statut <strong>Inscrit</strong>, puis l’affectera au groupe choisi.
          </div>
        </form>
      )}
    </DrawerShell>
  );
}